import { useState, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import BeltBadge from '../../components/common/BeltBadge';
import { SkeletonRow } from '../../components/common/Skeleton';
import { MdSearch, MdPeople, MdNote, MdClose, MdExpandMore, MdExpandLess, MdWarning, MdPhone, MdSchool, MdContentCopy, MdPersonAdd } from 'react-icons/md';
import api from '../../services/api';
import { useCalendar } from '../../context/CalendarContext';
import DualDate from '../../components/common/DualDate';
import { getMyStudents } from '../../services/enrollmentService';
import useClubVocab from '../../hooks/useClubVocab';
import { getStudentById } from '../../services/studentService';
import { getBeltStyle } from '../../utils/beltStyle';
import RecognizeModal from '../../components/common/RecognizeModal';
import AddStudentModal from '../../components/students/AddStudentModal';
import { getDisplayTxId } from '../../utils/displayId';
import { calcAge } from '../../utils/age';

const DAYS_SHORT = { Sun:'Sun', Mon:'Mon', Tue:'Tue', Wed:'Wed', Thu:'Thu', Fri:'Fri', Sat:'Sat',
  Sunday:'Sun', Monday:'Mon', Tuesday:'Tue', Wednesday:'Wed', Thursday:'Thu', Friday:'Fri', Saturday:'Sat' };

const timeStr = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2,'0')} ${h >= 12 ? 'PM' : 'AM'}`;
};

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

function AttRing({ pct }) {
  const r = 24, stroke = 5;
  const circ = 2 * Math.PI * r;
  const offset = circ - (Math.min(pct, 100) / 100) * circ;
  const color = pct >= 80 ? '#22c55e' : pct >= 60 ? '#f59e0b' : '#ef4444';
  return (
    <svg width={60} height={60}>
      <circle cx={30} cy={30} r={r} fill="none" stroke="#e5e7eb" strokeWidth={stroke} />
      <circle cx={30} cy={30} r={r} fill="none" stroke={color} strokeWidth={stroke}
        strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
        transform="rotate(-90 30 30)" />
      <text x="50%" y="50%" dominantBaseline="middle" textAnchor="middle"
        fontSize="12" fontWeight="bold" fill={color}>{pct}%</text>
    </svg>
  );
}

// ── Trainer's limited view of a student profile ──────────

function StudentProfileDrawer({ profileId, name, onClose }) {
  const v = useClubVocab();
  const [showRecognize, setShowRecognize] = useState(false);
  const { data, isPending } = useQuery({
    queryKey: ['trainer-student-profile', profileId],
    queryFn:  () => getStudentById(profileId).then(r => r.data.student),
    staleTime: 60000,
    enabled:  !!profileId,
  });

  const p   = data || {};
  const usr = p.user || {};
  const age = calcAge(p.dateOfBirth);
  const enrollments  = p.enrollments || [];
  const attSummary   = p.attendanceSummary;

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-full max-w-sm bg-white h-full flex flex-col shadow-2xl">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
          <h2 className="font-bold text-gray-800 text-sm">{v.member} Profile</h2>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100">
            <MdClose className="text-xl" />
          </button>
        </div>

        {isPending ? (
          <div className="p-5 space-y-3">
            {[1,2,3,4].map(i => <div key={i} className="h-10 bg-gray-100 rounded-xl animate-pulse" />)}
          </div>
        ) : (
          <>
          <div className="flex-1 overflow-y-auto p-5 space-y-5">
            {/* Identity */}
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-primary-light text-primary rounded-full flex items-center justify-center text-xl font-bold flex-shrink-0">
                {(usr.name || name || '?')[0].toUpperCase()}
              </div>
              <div className="min-w-0">
                <p className="font-bold text-gray-800 truncate">{usr.name || name}</p>
                {usr.phone && (
                  <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                    <MdPhone className="text-gray-300" /> {usr.phone}
                  </p>
                )}
                <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${getBeltStyle(p.currentGrade)}`}>
                    {p.currentGrade || 'White'} Belt
                  </span>
                  {age != null && (
                    <span className="text-xs text-gray-400">{age} yrs</span>
                  )}
                  {p.discipline && (
                    <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full">{p.discipline}</span>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-1.5 mt-1">
                  {p.dateOfBirth ? (
                    <>
                      <DualDate date={p.dateOfBirth} className="text-xs text-muted" />
                      {p.ageGroup && (
                        <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                          p.ageGroup === 'Too young' ? 'bg-nav-hover text-muted' : 'bg-nav-hover text-secondary'
                        }`}>
                          {p.ageGroup}
                        </span>
                      )}
                      {/* Read-only — resolving the review is an admin action (Students page) */}
                      {p.classPlacementReviewRequired && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-500/15 text-amber-400 border border-amber-500/20">
                          <MdWarning className="text-[10px]" /> Class review needed
                        </span>
                      )}
                    </>
                  ) : (
                    <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold bg-nav-hover text-muted">No DOB</span>
                  )}
                </div>
                {getDisplayTxId(p) !== 'ID pending' && (
                  <button
                    type="button"
                    title="Click to copy TrainAxis ID"
                    onClick={() => {
                      navigator.clipboard.writeText(getDisplayTxId(p)).catch(() => {});
                      toast.success('ID copied!', { duration: 1500 });
                    }}
                    className="flex items-center gap-1 font-mono text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-md hover:bg-gray-200 transition-colors mt-1.5"
                  >
                    {getDisplayTxId(p)} <MdContentCopy className="text-[10px] opacity-60" />
                  </button>
                )}
              </div>
            </div>

            {/* Medical info */}
            {p.medicalInfo ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3">
                <div className="flex items-center gap-2 mb-1">
                  <MdWarning className="text-yellow-500" />
                  <span className="text-xs font-semibold text-yellow-700">Medical Information</span>
                </div>
                <p className="text-sm text-yellow-800">{p.medicalInfo}</p>
              </div>
            ) : (
              <div className="border border-dashed border-gray-200 rounded-xl p-3 text-center">
                <p className="text-xs text-gray-400">No medical conditions on file</p>
              </div>
            )}

            {/* Emergency contact */}
            {(p.emergencyContact?.name || p.emergencyContact?.phone) && (
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Emergency Contact</p>
                <div className="bg-gray-50 rounded-xl p-3 space-y-1">
                  <p className="text-sm font-semibold text-gray-800">{p.emergencyContact.name}</p>
                  {p.emergencyContact.relationship && (
                    <p className="text-xs text-gray-500">{p.emergencyContact.relationship}</p>
                  )}
                  {p.emergencyContact.phone && (
                    <p className="text-xs text-gray-600 flex items-center gap-1">
                      <MdPhone className="text-gray-300" /> {p.emergencyContact.phone}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Attendance this month */}
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Attendance — This Month</p>
              {attSummary ? (
                <div className="flex items-center gap-4 bg-gray-50 rounded-xl p-3">
                  <AttRing pct={attSummary.percentage} />
                  <div className="flex-1 space-y-1">
                    {[
                      { label: 'Present', val: attSummary.thisMonth.present, color: 'text-green-600' },
                      { label: 'Late',    val: attSummary.thisMonth.late,    color: 'text-amber-500' },
                      { label: 'Absent',  val: attSummary.thisMonth.absent,  color: 'text-red-500'   },
                    ].map(({ label, val, color }) => (
                      <div key={label} className="flex justify-between items-center">
                        <span className="text-xs text-gray-500">{label}</span>
                        <span className={`text-sm font-bold ${color}`}>{val}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-400">No records this month</p>
              )}
            </div>

            {/* Class enrollments */}
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                Classes ({enrollments.length})
              </p>
              {enrollments.length === 0 ? (
                <div className="border border-dashed border-gray-200 rounded-xl py-6 text-center">
                  <MdSchool className="text-2xl text-gray-200 mx-auto mb-1" />
                  <p className="text-xs text-gray-400">Not enrolled in any class</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {enrollments.map(e => {
                    const cls = e.classId;
                    if (!cls) return null;
                    return (
                      <div key={e._id} className="flex items-center justify-between bg-gray-50 rounded-xl px-3 py-2.5">
                        <p className="text-sm font-medium text-gray-800">{cls.name || cls}</p>
                        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                          e.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                        }`}>{e.status}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Recognize action */}
          <div className="px-5 pb-5 pt-2 border-t border-gray-100 flex-shrink-0">
            <button
              onClick={() => setShowRecognize(true)}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-purple-200 rounded-xl text-sm text-purple-700 hover:bg-purple-50 transition-colors font-semibold"
            >
              🎖️ Recognize {v.member}
            </button>
          </div>
          </>
        )}
      </div>

      {showRecognize && (
        <RecognizeModal
          studentProfileId={profileId}
          studentName={data?.user?.name || name}
          onClose={() => setShowRecognize(false)}
        />
      )}
    </div>
  );
}

function NotesModal({ student, onClose }) {
  const queryClient = useQueryClient();
  const { register, handleSubmit } = useForm({ defaultValues: { notes: student.profile?.notes || '' } });
  const [saving, setSaving] = useState(false);

  const onSubmit = async (data) => {
    setSaving(true);
    try {
      await api.put(`/api/students/${student.profile?._id}`, { notes: data.notes });
      toast.success('Notes saved');
      queryClient.invalidateQueries({ queryKey: ['trainer-students'] });
      onClose();
    } catch {
      toast.error('Failed to save notes');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="font-bold text-secondary text-base">Training Notes</h2>
            <p className="text-xs text-gray-400 mt-0.5">{student.name} · {student.profile?.currentGrade} Belt</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100">
            <MdClose className="text-xl" />
          </button>
        </div>
        <form onSubmit={handleSubmit(onSubmit)} className="px-6 py-4">
          <textarea
            {...register('notes')}
            rows={5}
            placeholder="Training observations, strengths, areas to improve…"
            className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
          />
          <div className="flex justify-end gap-2 mt-3">
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save Notes'}</Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ClassGroup({ classGroup, search, onViewProfile }) {
  const v = useClubVocab();
  const [expanded, setExpanded] = useState(true);
  const [notesStudent, setNotes] = useState(null);
  const { formatDate } = useCalendar();

  const filtered = search
    ? classGroup.students.filter(s => s.name?.toLowerCase().includes(search.toLowerCase()))
    : classGroup.students;

  const cls = classGroup.class;

  return (
    <Card className="p-0 overflow-hidden">
      <div
        onClick={() => setExpanded(e => !e)}
        className="flex items-center justify-between px-5 py-3.5 cursor-pointer hover:bg-gray-50 transition-colors border-b border-gray-100"
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-primary-light rounded-xl flex items-center justify-center flex-shrink-0">
            <MdPeople className="text-primary" />
          </div>
          <div>
            <p className="font-semibold text-secondary text-sm">{cls.name}</p>
            <p className="text-xs text-gray-400">
              {cls.days?.map(d => DAYS_SHORT[d]).join(', ')} · {timeStr(cls.startTime)} · {classGroup.students.length} {classGroup.students.length !== 1 ? v.members.toLowerCase() : v.member.toLowerCase()}
            </p>
          </div>
        </div>
        <div className="text-gray-400">
          {expanded ? <MdExpandLess /> : <MdExpandMore />}
        </div>
      </div>

      {expanded && (
        !filtered.length ? (
          <div className="py-8 text-center text-gray-400 text-sm">No {v.members.toLowerCase()} match your search</div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 text-xs text-gray-400 font-medium">
                <th className="text-left px-5 py-2.5">{v.member}</th>
                <th className="text-left px-4 py-2.5 hidden sm:table-cell">Belt</th>
                <th className="text-left px-4 py-2.5 hidden md:table-cell">Joined</th>
                <th className="text-left px-4 py-2.5">Status</th>
                <th className="text-left px-4 py-2.5">Notes</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(s => (
                <tr key={s.userId} onClick={() => onViewProfile(s)} className="border-t border-gray-50 hover:bg-gray-50 transition-colors cursor-pointer">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 bg-primary-light text-primary rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">
                        {(s.name || '?')[0].toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-secondary truncate">{s.name}</p>
                        <p className="text-xs text-gray-400 truncate">{s.profile?.currentGrade} Belt</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 hidden sm:table-cell">
                    <BeltBadge belt={s.profile?.currentGrade} size="xs" />
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell text-xs text-gray-500">
                    {s.profile?.joinDate ? <DualDate date={s.profile.joinDate} /> : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${s.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                      {s.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={(e) => { e.stopPropagation(); setNotes(s); }}
                      className="flex items-center gap-1 text-xs text-gray-400 hover:text-primary transition-colors"
                    >
                      <MdNote /> {s.profile?.notes ? 'Edit' : 'Add'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      )}

      {notesStudent && <NotesModal student={notesStudent} onClose={() => setNotes(null)} />}
    </Card>
  );
}

function MyStudents() {
  const v = useClubVocab();
  document.title = `My ${v.members} — TrainAxis`;
  const [search,          setSearch]          = useState('');
  const [input,           setInput]           = useState('');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [addOpen,         setAddOpen]         = useState(false);
  const debounceRef = useRef(null);
  const queryClient = useQueryClient();

  const { data, isPending } = useQuery({
    queryKey: ['trainer-students'],
    queryFn:  () => getMyStudents().then(r => r.data),
  });

  const handleSearch = (val) => {
    setInput(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setSearch(val), 350);
  };

  const classGroups = data?.classes || [];
  const totalStudents = classGroups.reduce((sum, g) => sum + g.students.length, 0);

  if (isPending) {
    return (
      <div className="space-y-4">
        {Array.from({length:2}).map((_,i)=>(
          <Card key={i} className="p-0 overflow-hidden">
            <div className="h-14 bg-gray-50 animate-pulse" />
            <table className="w-full"><tbody>{Array.from({length:3}).map((_,j)=><SkeletonRow key={j} cols={5}/>)}</tbody></table>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-muted text-sm">
          {classGroups.length} class{classGroups.length !== 1 ? 'es' : ''} · {totalStudents} {totalStudents !== 1 ? v.members.toLowerCase() : v.member.toLowerCase()}
        </p>
        <Button onClick={() => setAddOpen(true)}>
          <MdPersonAdd className="text-lg" /> Add {v.member}
        </Button>
      </div>

      <Card>
        <div className="relative">
          <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg" />
          <input
            value={input}
            onChange={e => handleSearch(e.target.value)}
            placeholder="Search by name or email…"
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </div>
      </Card>

      {!classGroups.length ? (
        <Card>
          <div className="py-14 text-center">
            <MdPeople className="text-5xl text-gray-200 mx-auto mb-3" />
            <p className="text-gray-400 text-sm font-medium">No classes assigned yet</p>
            <p className="text-gray-300 text-xs mt-1">Contact admin to assign you to classes</p>
          </div>
        </Card>
      ) : (
        classGroups.map(group => (
          <ClassGroup
            key={group.class._id}
            classGroup={group}
            search={search}
            onViewProfile={setSelectedStudent}
          />
        ))
      )}

      {selectedStudent && (
        <StudentProfileDrawer
          profileId={selectedStudent.profile?._id}
          name={selectedStudent.name}
          onClose={() => setSelectedStudent(null)}
        />
      )}

      {addOpen && (
        <AddStudentModal
          isTrainer
          onClose={() => setAddOpen(false)}
          onCreated={() => queryClient.invalidateQueries({ queryKey: ['trainer-students'] })}
        />
      )}
    </div>
  );
}

export default MyStudents;
